import React, { useState, useEffect } from 'react'
import { Skeleton, Stack } from '@mui/material'
import { NodeRendererProps, Tree } from 'react-arborist'
import { Person, FolderOpen, Folder } from '@mui/icons-material'

import { PatientTreeNode } from '../../utils/types'
import '../../styles/css/tree.css'
import { bglightblue, muiblue } from '../../styles/js/colors.style'


interface NodeExtraProps {
    selectedNode: any
}

const Node = ({
    node,
    style,
    dragHandle,
    selectedNode
}: NodeRendererProps<any> & NodeExtraProps) => {
    if (node.data.id==='none') return null
    return (
        <div
            style={{
                ...style,
                background: node.isSelected ? bglightblue : "transparent",
            }}
            className='directory-node'
            ref={dragHandle}
            onClick={() => node.toggle()}
        >
            {node.isLeaf
                ? <Person sx={{color: 'grey'}} />
                : node.isOpen
                    ? <FolderOpen color="primary" />
                    : <Folder color="primary" />
            }
            <span className="node-text" style={{ color: selectedNode===node.data.name ? muiblue : 'unset' }}>
                {node.data.name}
            </span>
        </div>
    )
}

interface PatientTreeProps {
    patientDict: PatientTreeNode,
    width: number,
    height: number,
    selectNode: Function,
    focusNodeId: string,
    searchTerm: string
    selectedNode: string | null
}

const PatientTree: React.FC<PatientTreeProps> = ({
    patientDict, width, height, selectNode, focusNodeId, searchTerm, selectedNode
}) => {
    const [tree, setTree] = useState<PatientTreeNode[]>([])

    const rowHeight = 28

    useEffect(() => {
        if (!Object.keys(patientDict).length) return

        const worker = new Worker(new URL('../../workers/processTree.worker.ts', import.meta.url))
        worker.postMessage(patientDict)
        worker.onmessage = (event) => {
            if (event.data[0] && 'id' in event.data[0]) setTree(event.data)
        }
        worker.onerror = (error) => console.error('Worker error:', error)

        return () => worker.terminate()
    }, [patientDict])

    const onSelect = (nodes: any[]) => {
        if (nodes.length) {
            const node = nodes[0]
            if (node.isLeaf) {
                if (node.data.id==='none') return
                const key: string = node.data.full_path!.split(node.data.name)[0] + node.data.name
                selectNode(key)
            } else {
                const key: string = node.data.full_path!.split(node.data.name)[0] + node.data.name + '/'
                selectNode(key)
            }
        }
    }

    return (
        <>
        {tree.length
            ? <Tree
                data={tree}
                openByDefault={false}
                rowHeight={rowHeight}
                onSelect={onSelect}
                height={height}
                width={width}
                disableDrag={true}
                selection={focusNodeId}
                searchTerm={searchTerm}
            >
                {(props) => (
                    <Node
                        {...props}
                        selectedNode={selectedNode}
                    />
                )}
            </Tree>
            : <Stack spacing={1}>
                <Skeleton variant='rectangular' height={24} />
                <Skeleton variant='rectangular' height={24} />
                <Skeleton variant='rectangular' height={24} />
            </Stack>
        }
        </>
    )
    
}

export default PatientTree